import React from "react";
import { cn } from "../../DashboardPage/lib/utils";

/**
 * Loading placeholders.
 *
 * These replace the full-screen `<Loader />` on data-driven views. A centred
 * spinner blanks the whole page and gives no hint of what is arriving; a
 * skeleton keeps the layout stable, so content fills in rather than popping the
 * page around when the request lands.
 *
 * Everything here is decorative: the wrappers carry `aria-hidden`, and the
 * surrounding view is responsible for announcing loading state to assistive tech
 * (`aria-busy` on the region).
 */

export function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn("animate-pulse rounded-md bg-neutral-200", className)}
      {...props}
    />
  );
}

interface SkeletonTextProps {
  /** Number of lines. The last one is rendered short, like real text. */
  lines?: number;
  className?: string;
}

export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cn("space-y-2", className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn("h-3.5", i === lines - 1 ? "w-2/3" : "w-full")}
        />
      ))}
    </div>
  );
}

interface SkeletonTableRowsProps {
  rows?: number;
  /**
   * Column count, or the columns' alignments. Passing alignments keeps each
   * placeholder under its own header — a right-aligned Actions column that gets
   * a left-aligned placeholder leaves it stranded mid-row.
   */
  columns: number | Array<"left" | "right" | "center" | undefined>;
  /**
   * Cell padding. DataTable passes its own `dataTableCellClass`; the default
   * mirrors it for standalone use. Kept as a prop rather than an import so this
   * module doesn't form an import cycle with DataTable.
   */
  cellClassName?: string;
}

/**
 * Body rows shaped like real table rows — same cell padding and row divider as
 * DataTable, so the table doesn't resize when data arrives.
 */
export function SkeletonTableRows({
  rows = 5,
  columns,
  cellClassName = "px-4 py-3",
}: SkeletonTableRowsProps) {
  const aligns = typeof columns === "number"
    ? (Array.from({ length: columns }) as Array<undefined>)
    : columns;

  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-b border-neutral-100 last:border-b-0">
          {aligns.map((align, c) => (
            <td key={c} className={cellClassName}>
              {/*
                The placeholder is a block, so it is positioned with auto
                margins rather than `text-align` — which only moves inline
                content and would leave it flush left regardless.
              */}
              <Skeleton
                className={cn(
                  "h-3.5",
                  c === 0 ? "w-32" : "w-20",
                  align === "right" && "ml-auto",
                  align === "center" && "mx-auto"
                )}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

interface SkeletonCardsProps {
  count?: number;
  className?: string;
}

/** A row of stat-card placeholders, matching StatCard's shape. */
export function SkeletonStatCards({ count = 3, className }: SkeletonCardsProps) {
  return (
    <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-3", className)} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-3.5 py-3 bg-white border rounded-xl border-neutral-200"
        >
          <Skeleton className="w-8 h-8 rounded-full shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="w-20 h-3" />
            <Skeleton className="w-12 h-5" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface SkeletonFieldsProps {
  count?: number;
  columns?: 1 | 2 | 3;
  className?: string;
}

/** Label/value placeholders for a detail grid (see DetailField). */
export function SkeletonFields({ count = 6, columns = 3, className }: SkeletonFieldsProps) {
  const colClass =
    columns === 1 ? "grid-cols-1" : columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3";
  return (
    <div className={cn("grid grid-cols-1 gap-x-6 gap-y-5", colClass, className)} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="w-24 h-2.5" />
          <Skeleton className="w-40 h-4" />
        </div>
      ))}
    </div>
  );
}

/** A full card placeholder: heading, then body lines. */
export function SkeletonCard({
  lines = 4,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div
      className={cn("p-4 bg-white border rounded-lg border-neutral-200 sm:p-6", className)}
      aria-hidden="true"
    >
      <Skeleton className="w-40 h-5 mb-4" />
      <SkeletonText lines={lines} />
    </div>
  );
}

/**
 * Placeholder for a whole in-app page while the route itself is waiting (auth
 * check, lazy chunk): breadcrumb, title + description, then a table card —
 * the shape most pages share. Renders inside the shell, so the sidebar stays.
 */
export function PageSkeleton() {
  return (
    <div className="py-2 sm:p-6" aria-busy="true" aria-label="Loading page">
      <Skeleton className="w-40 h-4 mb-4" />
      <Skeleton className="h-7 mb-2 w-60" />
      <Skeleton className="w-80 h-4 mb-6 max-w-full" />
      <div className="overflow-hidden bg-white border rounded-lg border-neutral-200">
        <div className="px-4 py-3 border-b border-neutral-200 bg-primary-50/40">
          <Skeleton className="w-1/3 h-3" />
        </div>
        <table className="min-w-full">
          <tbody className="divide-y divide-neutral-100">
            <SkeletonTableRows rows={6} columns={4} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Placeholder for the standalone centred-card pages (login, sign-up, reset,
 * pricing hand-offs) while their code downloads: same card, same position, so
 * the form drops into place instead of replacing a full-screen spinner.
 */
export function AuthCardSkeleton() {
  return (
    <div
      className="flex items-center justify-center min-h-screen px-4 py-12 bg-neutral-50"
      aria-busy="true"
      aria-label="Loading"
    >
      <div className="w-full max-w-md p-8 bg-white border rounded-lg shadow-sm border-neutral-200">
        <Skeleton className="w-56 h-7 mx-auto mb-8" />
        <div className="space-y-5">
          {[0, 1].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="w-16 h-3.5" />
              <Skeleton className="w-full h-10" />
            </div>
          ))}
          <Skeleton className="w-full h-10 mt-2" />
          <Skeleton className="w-48 h-3.5 mx-auto" />
        </div>
      </div>
    </div>
  );
}
